// React
import { useContext } from "react"

// Components
import { CancelButton, CommandButton, Loading } from "components"
import { ActionButton } from "../components"
import { AttachmentModal, InputSection, PrintPaymentBill } from "./components"

// Contexts
import { AccountProvider } from "contexts"
import { BillContext } from "./contexts"

// Form
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Utils
import { convertNumber, useApi, useToggle } from "utils"
import { FormType, useBillDetail, validationSchema } from "./utils"

export default function CreatePaymentBill(): JSX.Element {
    // Hooks
    const { bill_id } = useParams<{ bill_id: string }>()
    return (
        <PaymentBillCreate
            bill_id={bill_id!}
        />
    )
}

export function PaymentBillCreate(params: {
    bill_id: string
}): JSX.Element {
    // Hooks
    const { data, isLoading } = useBillDetail(params.bill_id)

    return (
        <BillContext.Provider
            value={data ?? {
                approve_by: null,
                approve_status: 0,
                bill_id: 0,
                coa_id: "",
                deposit_amount: 0,
                memo: "",
                payment_amount: 0,
                purchase_order_id: 0,
                referensi: "",
                transaction_date: "",
                trx_code: "",
                databill: {
                    address: "",
                    balance: 0,
                    location_id: "",
                    saldo_deposit: 0,
                    trx_code: "",
                    vendor_id: "",
                    vendor_name: ""
                }
            }}
        >
            <AccountProvider>
                {isLoading || !data ? (
                    <Loading loading={isLoading} />
                ) : (
                    <FormSection />
                )}
            </AccountProvider>
        </BillContext.Provider>
    )
}

function FormSection(): JSX.Element {
    // Hooks
    const api = useApi()
    const data = useContext(BillContext)
    const navigate = useNavigate()
    const { isActive, toggle } = useToggle(false)

    // Vars
    const databill = data.databill
    const balance: number = databill.balance
    const saldo_deposit: number = databill.saldo_deposit
    const deposit_amount: number = saldo_deposit < 0 ? 0 : saldo_deposit > balance ? balance : saldo_deposit
    const bill_id = data.bill_id

    // Form
    const defaultValues: FormType = {
        deposit_amount,
        _balance: 0,
        coa_id: "",
        memo: "Payment Bill transaction",
        payment_amount: 0,
        referensi: "",
        trx_code: "",
        transaction_date: moment().format("YYYY-MM-DD"),
        vendor_id: databill.vendor_id,
        imgPath: []
    }
    const methods = useForm<FormType>({
        defaultValues,
        resolver: yupResolver(validationSchema)
    })
    const onSubmit = (value: FormType) => {
        // Vars
        const final_value = {
            bill_id,
            trx_code: value.trx_code,
            vendor_id: value.vendor_id,
            location_id: databill.location_id,
            address: databill.address,
            memo: value.memo,
            transaction_date: value.transaction_date,
            referensi: value.referensi,
            coa_id: value.coa_id,
            deposit_amount: value.deposit_amount,
            payment_amount: value.payment_amount,
            imgPath: value.imgPath || []
        }
        const isBelowBalance: boolean =
            databill.balance - value.deposit_amount - value.payment_amount < 0

        return new Promise<void>((resolve) => {
            // Functions
            const submitForm = (): void => {
                toast
                    .promise(api.post("/paymentbill/add", final_value), {
                        loading: "Loading...",
                        success: (res) => res.data.message,
                        error: (err) =>
                            err.response.data.detail?.message ?? err.response.data.message
                    })
                    .then(() => {
                        navigate(`/purchase/transaction/purchase-order/${data.purchase_order_id}`)
                    })
                    .catch(() => { })
                    .finally(resolve)
            }

            if (isBelowBalance) {
                toast.error("Payment Deposit & Payment Amount cannot be more than Balance Bill")
                return resolve()
            } else if (value.deposit_amount > saldo_deposit) {
                toast.error(`Payment Deposit cannot be more than The Deposit Balance (Current balance: ${convertNumber(saldo_deposit).intoNormalAmount})`)
                return resolve()
            }

            submitForm()
        })
    }

    const trxCode = localStorage.getItem("trx_code_payment_bill")

    return (
        <FormProvider {...methods}>
            <InputSection
                buttonsection={
                    <ActionButton>
                        <section className="flex flex-wrap gap-3">
                            <CommandButton actiontype="print" onClick={toggle} />
                            <AttachmentModal code={trxCode!} />

                            {isActive ? (<PrintPaymentBill toggle={toggle} />) : null}
                        </section>

                        <section className="flex flex-wrap gap-3">
                            <CancelButton />

                            <CommandButton
                                actiontype="save"
                                loading={methods.formState.isSubmitting ? "true" : undefined}
                                onClick={methods.handleSubmit(onSubmit)}
                            />
                        </section>
                    </ActionButton>
                }
            />
        </FormProvider>
    )
}
